import React, { useRef } from "react";
import ReactDOM from "react-dom";
import { useWindowSize } from "react-use";
import styles from "./side-menu-overflow.module.css";
import sideMenuStyles from "../side-menu.module.css";
import { Floater } from "frontend/ui-components/floaters/floater";
import { PortalID } from "frontend/modal/portal-types";
import { MenuItem } from "../side-menu-items/side-menu-items";
import { useIsCIMReorderEnabled } from "utils/url-utils";
import classNames from "classnames";
import { useFloating, autoUpdate, offset, shift, FloatingPortal, arrow, FloatingArrow } from "@floating-ui/react";

export default function SideMenuOverflow({
  menuItems,
  renderMenuItem,
  relativeTo,
  onHover,
  onLeave,
}: {
  menuItems: MenuItem[];
  renderMenuItem: (item: MenuItem, ref: any, sideMenuOverflow: boolean) => void;
  relativeTo: React.RefObject<HTMLElement>;
  onHover?: () => void;
  onLeave?: () => void;
}) {
  const portal = document.getElementById(PortalID.Floaters);
  const isCimReorderEnabled = useIsCIMReorderEnabled();
  const arrowRef = useRef(null);

  const minScreenHeight = 310;
  const size = useWindowSize();

  // Set up floating UI
  const { refs, floatingStyles, context } = useFloating({
    open: isCimReorderEnabled && !!relativeTo.current,
    placement: "top-end",
    middleware: [offset({ mainAxis: 15 }), shift({ padding: 50 }), arrow({ element: arrowRef })],
    whileElementsMounted: autoUpdate,
  });

  // Set the reference element when it changes
  React.useEffect(() => {
    if (relativeTo.current) {
      refs.setReference(relativeTo.current);
    }
  }, [refs, relativeTo]);

  // Render content function
  const renderContent = () => (
    <div className={classNames(styles.itemsContainer, isCimReorderEnabled && styles.cimItemsContainer)}>
      {menuItems.map((item) => renderMenuItem(item, null, true))}
    </div>
  );

  // When CIM footer is enabled, use FloatingPortal
  if (isCimReorderEnabled && relativeTo.current) {
    return (
      <FloatingPortal id={PortalID.Floaters}>
        <div
          ref={refs.setFloating}
          style={{ ...floatingStyles, zIndex: 1001 }}
          className={classNames(styles.container, styles.cimContainer)}
          onMouseEnter={onHover}
          onMouseLeave={onLeave}
        >
          {renderContent()}
          <FloatingArrow ref={arrowRef} context={context} fill="white" width={14} height={7} />
        </div>
      </FloatingPortal>
    );
  }

  // Original logic for non-CIM footer
  const element = relativeTo.current ? (
    <>
      <Floater
        relativeTo={relativeTo}
        side={"right"}
        alignment={"middle"}
        extraStyle={size.height < minScreenHeight ? { zIndex: 1001, top: "20px" } : { zIndex: 1001 }}
      >
        <div className={styles.container} onMouseOver={onHover} onMouseEnter={onHover} onMouseLeave={onLeave}>
          {renderContent()}
        </div>
      </Floater>
      <Floater
        relativeTo={relativeTo}
        side={"right"}
        alignment={"middle"}
        extraStyle={{
          zIndex: 1001,
          transition: "opacity 0.1s ease-out, transform 0.1s ease-out",
          marginLeft: "-6px",
        }}
      >
        <div className={sideMenuStyles.arrow} />
      </Floater>
    </>
  ) : null;

  return portal ? ReactDOM.createPortal(element, portal) : element;
}
